import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Loading } from '../../components/office';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Switch } from '@verlo/shared/components/ui/switch';
import { Textarea } from '@verlo/shared/components/ui/textarea';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import { promotionsApi, menuApi, menuSectionsApi, sitesApi } from '@verlo/shared/services/api';
import {
  MECHANICS, KINDS, EMPTY_PROMOTION, describe, sectionLabel, scheduleWindows,
} from '@verlo/shared/lib/promotions';
import { toast } from 'sonner';
import { Loader2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@verlo/shared/components/ui/dialog';
import HoursEditor, { normaliseHours } from '../../components/menu/HoursEditor';
import { listOf, rightNow, venueNow } from '../../lib/promoLists';

/**
 * Menu › Promotions › one promotion (Back office sweep, Oct 2026: D9, mock "Promotion"). Its
 * own page, with a way back. Its name is the title, and the sentence under it always says the
 * whole thing. Then the sections in the order you'd think it through: what it's called, what
 * it does (the kind, in plain words with an example each), what it applies to, where it runs,
 * when (the same hours editor as a dish), and the small print. Pause and End are at the top,
 * and each says what it does first. Save publishes to the tills on the next down-sync.
 */

const Section = ({ n, title, hint, children, testId }) => (
  <section className="border-t border-hairline py-5 first:border-t-0" data-testid={testId}>
    <div className="mb-3 flex items-baseline gap-2.5">
      {n && <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-verlo-tint font-heading text-[12px] font-extrabold text-verlo">{n}</span>}
      <h2 className="font-heading text-[16px] font-extrabold text-ink">{title}</h2>
      {hint && <span className="ml-auto text-[12px] text-ink-faint">{hint}</span>}
    </div>
    {children}
  </section>
);

const Field = ({ label, children, hint }) => (
  <div className="grid grid-cols-[120px_1fr] items-center gap-3 py-1.5">
    <Label className="text-[12.5px] text-ink-soft">{label}</Label>
    <div className="flex flex-wrap items-center gap-2 text-[13px]">{children}{hint && <span className="text-[12px] text-ink-faint">{hint}</span>}</div>
  </div>
);

const Chip = ({ on, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-full border px-2.5 py-1 text-[12.5px] transition-colors ${on ? 'border-verlo bg-verlo text-white' : 'border-hairline-strong bg-white text-ink hover:border-verlo'}`}
    data-testid={testId}
  >
    {children}
  </button>
);

const Money = ({ value, onChange, testId, placeholder = '0.00' }) => (
  <div className="relative w-28">
    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft">£</span>
    <Input type="number" inputMode="decimal" step="0.01" min="0" value={value ?? ''} onChange={(e) => onChange(e.target.value)} className="pl-7 font-mono tabular-nums" placeholder={placeholder} data-testid={testId} />
  </div>
);

/** Sections as chips plus items by search — one selector. */
const Selector = ({ value, onChange, sections, items, label, testId }) => {
  const [q, setQ] = useState('');
  const sel = value || { items: [], sections: [] };
  const byId = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  const matches = q.trim().length >= 2
    ? items.filter((i) => i.name.toLowerCase().includes(q.trim().toLowerCase()) && !sel.items.includes(i.id)).slice(0, 8)
    : [];
  const toggleSection = (name) => onChange({
    ...sel, sections: sel.sections.includes(name) ? sel.sections.filter((s) => s !== name) : [...sel.sections, name],
  });
  return (
    <div data-testid={testId}>
      {label && <p className="mb-1.5 text-[12px] font-semibold text-ink-soft">{label}</p>}
      <div className="flex flex-wrap gap-1.5">
        {sections.map((s) => (
          <Chip key={s} on={sel.sections.includes(s)} onClick={() => toggleSection(s)} testId={`${testId}-section-${s}`}>{sectionLabel(s)}</Chip>
        ))}
      </div>
      <div className="mt-2 grid grid-cols-[120px_1fr] items-start gap-3">
        <Label className="pt-2 text-[12.5px] text-ink-soft">Also these</Label>
        <div>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the menu…" className="h-9 max-w-sm" data-testid={`${testId}-search`} />
          {matches.length > 0 && (
            <div className="mt-1 max-w-sm rounded-md border border-hairline bg-white shadow-float">
              {matches.map((m) => (
                <button key={m.id} type="button" className="flex w-full items-center justify-between px-3 py-1.5 text-left text-[13px] hover:bg-hover-tint" onClick={() => { onChange({ ...sel, items: [...sel.items, m.id] }); setQ(''); }}>
                  <span>{m.name}</span><span className="text-ink-faint">{sectionLabel(m.category)}</span>
                </button>
              ))}
            </div>
          )}
          {sel.items.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {sel.items.map((id) => (
                <span key={id} className="inline-flex items-center gap-1 rounded-full border border-verlo bg-verlo-tint px-2.5 py-0.5 text-[12.5px] text-verlo">
                  {byId[id]?.name || id}
                  <button type="button" onClick={() => onChange({ ...sel, items: sel.items.filter((i) => i !== id) })} aria-label="Remove"><X size={12} /></button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const numOrNull = (v) => (v === '' || v == null ? null : Number(v));

// The times live in `windows` (several days, several a day); the server writes the first
// back to days/time_start/time_end for anything that still reads only those.
const toPayload = (f) => ({
  name: f.name, mechanic: f.mechanic, kind: f.kind, status: f.status, site_ids: f.site_ids,
  schedule: {
    start: f.schedule.start || null, end: f.schedule.end || null,
    days: [], time_start: null, time_end: null,
    windows: normaliseHours(f.schedule.windows).map((w) => ({ days: w.days || [], time_start: w.time_start || null, time_end: w.time_end || null })),
  },
  applies: f.applies, rate: numOrNull(f.rate), amount: numOrNull(f.amount), price: numOrNull(f.price),
  buy: numOrNull(f.buy), free: numOrNull(f.free),
  sets: f.sets, trigger: f.trigger, reward: f.reward,
  needs_manager: !!f.needs_manager, code: f.code || null,
  redemption_limit: numOrNull(f.redemption_limit), max_per_bill: numOrNull(f.max_per_bill),
  supplier: f.kind === 'supplier' ? { name: f.supplier?.name || '', funding_per_unit: Number(f.supplier?.funding_per_unit) || 0 } : null,
  notes: f.notes || '',
});

const fromRow = (r) => ({
  ...EMPTY_PROMOTION, ...r,
  schedule: {
    ...EMPTY_PROMOTION.schedule, ...(r.schedule || {}),
    // An older offer's one window, as the editor's list (empty when it runs all the time).
    windows: scheduleWindows(r.schedule || {}).filter((w) => (w.days || []).length || w.time_start || w.time_end),
  },
  applies: r.applies || EMPTY_PROMOTION.applies,
  sets: (r.sets && r.sets.length >= 2) ? r.sets : EMPTY_PROMOTION.sets,
  trigger: r.trigger || EMPTY_PROMOTION.trigger, reward: r.reward || EMPTY_PROMOTION.reward,
  rate: r.rate ?? '', amount: r.amount ?? '', price: r.price ?? '', buy: r.buy ?? 2, free: r.free ?? 1,
  code: r.code || '', redemption_limit: r.redemption_limit ?? '', max_per_bill: r.max_per_bill ?? '',
  supplier: r.supplier || EMPTY_PROMOTION.supplier, notes: r.notes || '',
});

const STATUS_TAG = { live: 'Live', scheduled: 'Scheduled', drafts: 'Draft', ended: 'Ended' };

const PromotionEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const [form, setForm] = useState(EMPTY_PROMOTION);
  const [row, setRow] = useState(null);
  const [items, setItems] = useState([]);
  const [sections, setSections] = useState([]);
  const [sites, setSites] = useState([]);
  const [running, setRunning] = useState(false);
  const [clock, setClock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [asking, setAsking] = useState(null);   // 'paused' | 'ended' while its warning is up

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      menuApi.getAll().catch(() => ({ data: [] })),
      menuSectionsApi.list().catch(() => ({ data: [] })),
      sitesApi.list().catch(() => ({ data: [] })),
      isNew ? Promise.resolve(null) : promotionsApi.get(id),
      isNew ? Promise.resolve(null) : promotionsApi.live().catch(() => null),
      promotionsApi.usage({ days: 1 }).catch(() => null),
    ]).then(([m, s, st, p, l, u]) => {
      if (cancelled) return;
      setItems((m.data || []).filter((i) => i.is_available !== false));
      setSections((s.data || []).filter((x) => x.is_active !== false).map((x) => x.name));
      setSites(st.data?.sites || st.data || []);
      if (p) { setRow(p.data); setForm(fromRow(p.data)); }
      setRunning(!!(l?.data?.promotions || []).find((x) => x.id === id));
      if (u?.data) setClock({ today: u.data.today, tz: u.data.tz, dayStart: u.data.day_start });
    }).catch(() => toast.error("Couldn't load the promotion"))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, isNew]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setSched = (patch) => setForm((f) => ({ ...f, schedule: { ...f.schedule, ...patch } }));
  const itemNames = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i.name])), [items]);
  const payload = toPayload(form);
  const words = describe(payload, itemNames);
  const m = form.mechanic;
  const now = venueNow(clock?.tz);
  const today = clock?.today || `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const list = row ? listOf(row, today) : 'drafts';
  const next = row && list === 'live' ? rightNow(row, now, new Set(running ? [row.id] : []), clock?.dayStart) : null;
  // Whether it's running now, asked again after it's saved or its status changes.
  const refreshRunning = (pid) => promotionsApi.live()
    .then((l) => setRunning(!!(l.data?.promotions || []).find((x) => x.id === pid))).catch(() => {});

  const save = async (status) => {
    const body = toPayload({ ...form, status: status || form.status });
    if (!body.name.trim()) { toast.error('A promotion needs a name'); return; }
    setSaving(true);
    try {
      const res = isNew ? await promotionsApi.create(body) : await promotionsApi.update(id, body);
      toast.success(status === 'draft' ? 'Saved as a draft' : `${res.data?.name || 'Promotion'} saved`);
      if (isNew) navigate(`/back-office/promotions/${res.data.id}`, { replace: true });
      else { setRow(res.data); setForm(fromRow(res.data)); refreshRunning(res.data.id); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the promotion");
    } finally { setSaving(false); }
  };

  const setStatus = async (status) => {
    setAsking(null);
    try {
      const { data } = await promotionsApi.setStatus(id, status);
      // Only the status: anything typed and not yet saved stays as it was.
      setRow(data); setForm((f) => ({ ...f, status: data.status }));
      refreshRunning(data.id);
      toast.success(status === 'paused' ? `${data.name} is paused` : status === 'ended' ? `${data.name} has ended` : `${data.name} is live`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't change it"); }
  };

  if (loading) return <Loading />;
  const title = form.name.trim() || (isNew ? 'New promotion' : row?.name);

  return (
    <div className="mx-auto max-w-4xl" data-testid="promotion-editor">
      <Link to="/back-office/promotions" className="text-[13px] text-ink-soft hover:text-ink">‹ Promotions</Link>
      <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            <h1 className="font-heading text-[26px] font-extrabold text-ink" data-testid="promo-title">{title}</h1>
            {row && <span className="border border-hairline-strong bg-surface-2 px-2 py-0.5 font-heading text-[11px] font-bold uppercase tracking-[0.06em] text-ink-soft">{row.status === 'paused' ? 'Paused' : STATUS_TAG[list]}</span>}
          </div>
          <p className="mt-1 text-[14px] text-ink-soft" data-testid="promo-words">
            <b className="font-semibold text-ink">{words ? words[0].toUpperCase() + words.slice(1) : 'Pick what it does'}</b>
            {next && <span> {next.words === 'On now' ? 'On now.' : `${next.words}.`}</span>}
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          {row && row.status === 'live' && <Button variant="outline" onClick={() => setAsking('paused')} data-testid="promo-pause">Pause</Button>}
          {row && row.status === 'paused' && <Button variant="outline" onClick={() => setStatus('live')} data-testid="promo-resume">Resume</Button>}
          {row && row.status !== 'ended' && <Button variant="outline" className="text-signal-stop hover:text-signal-stop" onClick={() => setAsking('ended')} data-testid="promo-end">End</Button>}
          {(isNew || row?.status === 'draft') && <Button variant="outline" onClick={() => save('draft')} disabled={saving} data-testid="promo-save-draft">Save as draft</Button>}
          <Button className="bg-verlo hover:bg-verlo-hover" onClick={() => save(isNew || row?.status === 'draft' ? 'live' : undefined)} disabled={saving} data-testid="promo-save-live">
            {saving && <Loader2 size={14} className="mr-2 animate-spin" />}{isNew || row?.status === 'draft' ? 'Save and go live' : 'Save'}
          </Button>
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-hairline bg-white px-5">
        <Section n={1} title="What it's called" testId="promo-name-section">
          <Input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Wine Wednesday" className="h-10 max-w-md" data-testid="promo-name" autoFocus={isNew} />
          <p className="mt-1.5 text-[12.5px] text-ink-faint">Shown on the bill and in reports.</p>
        </Section>

        <Section n={2} title="What it does" testId="promo-what">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {MECHANICS.map((mc) => (
              <button key={mc.value} type="button" onClick={() => set({ mechanic: mc.value })} aria-pressed={m === mc.value}
                className={`rounded-md border px-3 py-2.5 text-left ${m === mc.value ? 'border-verlo bg-verlo-tint' : 'border-hairline bg-white hover:bg-hover-tint'}`}
                data-testid={`promo-mechanic-${mc.value}`}>
                <p className={`text-[14px] font-semibold ${m === mc.value ? 'text-verlo' : 'text-ink'}`}>{mc.label}</p>
                <p className="text-[12.5px] text-ink-faint">{mc.example}</p>
              </button>
            ))}
          </div>
          <div className="mt-3">
            {m === 'rate_off' && <Field label="How much off"><Input type="number" inputMode="decimal" min="0" max="100" value={form.rate} onChange={(e) => set({ rate: e.target.value })} className="h-9 w-24 font-mono tabular-nums" placeholder="e.g. 20" data-testid="promo-rate" /><span className="text-ink-soft">% off each</span></Field>}
            {m === 'amount_off' && <Field label="How much off"><Money value={form.amount} onChange={(v) => set({ amount: v })} testId="promo-amount" /><span className="text-ink-soft">off each</span></Field>}
            {m === 'set_price' && <Field label="Its price"><Money value={form.price} onChange={(v) => set({ price: v })} testId="promo-price" /><span className="text-ink-soft">each, while it runs</span></Field>}
            {m === 'multi_buy' && (
              <Field label="Buy">
                <Input type="number" min="1" value={form.buy} onChange={(e) => set({ buy: e.target.value })} className="h-9 w-16 font-mono tabular-nums" data-testid="promo-buy" />
                <span className="text-ink-soft">and the cheapest</span>
                <Input type="number" min="1" value={form.free} onChange={(e) => set({ free: e.target.value })} className="h-9 w-16 font-mono tabular-nums" data-testid="promo-free" />
                <span className="text-ink-soft">free</span>
              </Field>
            )}
            {m === 'bundle' && <Field label="The bundle's price"><Money value={form.price} onChange={(v) => set({ price: v })} testId="promo-bundle-price" /><span className="text-ink-soft">for one of each</span></Field>}
            {m === 'bill' && (
              <Field label="Off the bill">
                <Segmented value={form.rate !== '' && form.rate != null ? 'percent' : 'fixed'} onChange={(v) => set(v === 'percent' ? { amount: '', rate: form.rate || 10 } : { rate: '', amount: form.amount || 5 })} options={[{ value: 'percent', label: '%' }, { value: 'fixed', label: '£' }]} />
                {form.rate !== '' && form.rate != null
                  ? <Input type="number" min="0" max="100" value={form.rate} onChange={(e) => set({ rate: e.target.value })} className="h-9 w-24 font-mono tabular-nums" data-testid="promo-rate" />
                  : <Money value={form.amount} onChange={(v) => set({ amount: v })} testId="promo-amount" />}
                <span className="text-[12.5px] text-ink-faint">a server taps it on</span>
              </Field>
            )}
          </div>
        </Section>

        {m !== 'bill' && (
          <Section n={3} title="What it applies to" hint="sections, then anything by name" testId="promo-which">
            {m === 'kids_eat_free' ? (
              <div className="space-y-4">
                <Selector label="With each of these" value={form.trigger} onChange={(v) => set({ trigger: v })} sections={sections} items={items} testId="promo-trigger" />
                <Selector label="One of these goes free (the cheapest first)" value={form.reward} onChange={(v) => set({ reward: v })} sections={sections} items={items} testId="promo-reward" />
              </div>
            ) : m === 'bundle' ? (
              <div className="space-y-4">
                {form.sets.map((st, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="flex-1"><Selector label={`One from set ${i + 1}`} value={st} onChange={(v) => set({ sets: form.sets.map((x, j) => (j === i ? v : x)) })} sections={sections} items={items} testId={`promo-set-${i}`} /></div>
                    {form.sets.length > 2 && <Button variant="ghost" size="sm" onClick={() => set({ sets: form.sets.filter((_, j) => j !== i) })}>Remove</Button>}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => set({ sets: [...form.sets, { items: [], sections: [] }] })}>Add a set</Button>
              </div>
            ) : (
              <Selector value={form.applies} onChange={(v) => set({ applies: v })} sections={sections} items={items} testId="promo-applies" />
            )}
          </Section>
        )}

        <Section n={m === 'bill' ? 3 : 4} title="Where it runs" testId="promo-where">
          <div className="flex flex-wrap gap-1.5">
            <Chip on={form.site_ids.length === 0} onClick={() => set({ site_ids: [] })} testId="promo-site-all">Every site</Chip>
            {sites.length > 1 && sites.map((st) => (
              <Chip key={st.id} on={form.site_ids.includes(st.id)} onClick={() => set({ site_ids: form.site_ids.includes(st.id) ? form.site_ids.filter((x) => x !== st.id) : [...form.site_ids, st.id] })} testId={`promo-site-${st.id}`}>{st.name}</Chip>
            ))}
          </div>
          <p className="mt-1.5 text-[12.5px] text-ink-faint">{m === 'bill' ? 'A server taps it from Offers on the till.' : 'The till applies it by itself when a bill qualifies: one offer per line, the best one for the guest.'}</p>
        </Section>

        <Section n={m === 'bill' ? 4 : 5} title="When it runs" hint="on the trading day" testId="promo-when">
          <HoursEditor
            value={form.schedule.windows}
            onChange={(w) => setSched({ windows: w })}
            words={{ always: 'Every day, all day. Add a time to make it a happy hour, a weekday lunch or a Sunday special.', set: 'Runs at these times, on the venue’s trading day: 01:00 on Saturday still counts as Friday night.' }}
          />
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[13px] text-ink-soft">
            <span>From</span>
            <Input type="date" value={form.schedule.start || ''} onChange={(e) => setSched({ start: e.target.value || null })} className="h-9 w-40" data-testid="promo-date-start" />
            <span>until</span>
            <Input type="date" value={form.schedule.end || ''} onChange={(e) => setSched({ end: e.target.value || null })} className="h-9 w-40" data-testid="promo-date-end" />
            <span className="text-[12.5px] text-ink-faint">Blank: from now, with no end date.</span>
          </div>
        </Section>

        <Section n={m === 'bill' ? 5 : 6} title="The small print" testId="promo-rules">
          {m !== 'bill' && <Field label="Most per bill" hint="units it can touch; blank for no limit"><Input type="number" min="1" value={form.max_per_bill} onChange={(e) => set({ max_per_bill: e.target.value })} className="h-9 w-20 font-mono tabular-nums" data-testid="promo-max" /></Field>}
          {m === 'bill' && (
            <>
              <Field label="Needs a manager"><Switch checked={!!form.needs_manager} onCheckedChange={(v) => set({ needs_manager: v })} data-testid="promo-needs-manager" /><span className="text-[12.5px] text-ink-faint">otherwise any server can tap it</span></Field>
              <Field label="Code" hint="blank: no code asked for"><Input value={form.code} onChange={(e) => set({ code: e.target.value.toUpperCase() })} className="h-9 w-40 uppercase tracking-wider" placeholder="e.g. SPRING10" data-testid="promo-code" /></Field>
              <Field label="Limit" hint="bills it can go on; blank for no limit"><Input type="number" min="1" value={form.redemption_limit} onChange={(e) => set({ redemption_limit: e.target.value })} className="h-9 w-24 font-mono tabular-nums" data-testid="promo-limit" /></Field>
            </>
          )}
          <Field label="Who it's for">
            <Segmented value={form.kind} onChange={(v) => set({ kind: v })} options={KINDS.map((k) => ({ value: k.value, label: k.label, testId: `promo-kind-${k.value}` }))} />
          </Field>
          {form.kind === 'supplier' && (
            <Field label="Supplier">
              <Input value={form.supplier?.name || ''} onChange={(e) => set({ supplier: { ...form.supplier, name: e.target.value } })} placeholder="e.g. Brooklyn Brewery" className="h-9 w-48" data-testid="promo-supplier" />
              <span className="text-ink-soft">funds</span>
              <Money value={form.supplier?.funding_per_unit} onChange={(v) => set({ supplier: { ...form.supplier, funding_per_unit: v } })} testId="promo-funding" />
              <span className="text-[12.5px] text-ink-faint">per unit</span>
            </Field>
          )}
          <Field label="Notes"><Textarea value={form.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="For the team, not shown to guests" className="h-16 max-w-md" data-testid="promo-notes" /></Field>
        </Section>
      </div>

      <Dialog open={!!asking} onOpenChange={(o) => { if (!o) setAsking(null); }}>
        <DialogContent className="max-w-sm" data-testid="promo-status-dialog">
          <DialogHeader>
            <DialogTitle>{asking === 'paused' ? `Pause ${row?.name}?` : `End ${row?.name}?`}</DialogTitle>
            <DialogDescription>
              {asking === 'paused'
                ? 'It stops on every till until you resume it. Its takings stay in the Promotions report.'
                : 'It stops on every till now, for good. Its takings stay in the Promotions report.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAsking(null)}>Cancel</Button>
            <Button className={asking === 'ended' ? 'bg-signal-stop text-white hover:bg-signal-stop/90' : 'bg-verlo hover:bg-verlo-hover'} onClick={() => setStatus(asking)} data-testid="promo-status-confirm">
              {asking === 'paused' ? 'Pause it' : 'End it'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PromotionEditorPage;
